import React from 'react';
import { TableColumn } from 'react-data-table-component';
import Swal from "sweetalert2";
import withReactContent from 'sweetalert2-react-content';

import { ShowBlocker } from '../../Components/LoaderComponent/LoaderComponent';
import './UserLoanRejection.css';
import { IUserLoanRejection, UserLoanRejectionProps, UserLoanRejectionState, IFileDocument, IDocumentGroup } from './interface';
import DataGrid from '../../Components/DataGrid/DataGrid';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import ProfileServices from '../../Services/ProfileServices';

const MySwal = withReactContent(Swal);

class UserLoanRejection extends React.Component<UserLoanRejectionProps, UserLoanRejectionState> {

    constructor(props_: any) {
        super(props_);

        this.state = {
            accessToken: '',
            currentPage: 1,
            currentRowsPerPage: 10,
            searchText: '',
            totalRows: 0,
            rejectedLoans: [],
            currentSortColumn: '',
            sortingDirection: ''
        }
    }

    private formColumns: TableColumn<IUserLoanRejection>[] = [
        {
            name: 'Taxpayer Name',
            selector: (row: IUserLoanRejection) => row.userName,
            sortable: true,
        },
        {
            name: 'Email',
            selector: (row: IUserLoanRejection) => row.email,
            sortable: true,
        },
        {
            name: 'Mobile Number',
            selector: (row: IUserLoanRejection) => row.mobileNumber,
            sortable: true,
        },
        {
            name: 'Status',
            selector: (row: IUserLoanRejection) => row.status,
            sortable: true,
            cell: (row: IUserLoanRejection) => (
                <span className={`status-badge ${row.status.toLowerCase()}`}>
                    {row.status}
                </span>
            )
        },
        {
            name: 'Documents',
            cell: (row: IUserLoanRejection) => (
                <button
                    className='view-documents-btn'
                    onClick={(event) => {
                        this.viewDocuments(row);
                    }}>
                    View Documents ({this.getTotalDocumentsCount(row)})
                </button>
            ),
            allowOverflow: false,
            button: true,
            width: '150px'
        },
        {
            name: 'Action',
            cell: (row: IUserLoanRejection) => {
                const isDisabled = row.status === 'Rejected' || row.status === 'Approved' || row.status === 'InEligible';
                return (
                    <div className="action-buttons">
                        
                        <button
                            className={`approve-btn ${isDisabled ? 'disabled' : ''}`}
                            disabled={isDisabled}
                            onClick={(event) => {
                                if (!isDisabled) {
                                    this.handleStatusUpdate(row, 'Approved');
                                }
                            }}>
                            Approve
                        </button>
                        <button
                            className={`reject-btn ${isDisabled ? 'disabled' : ''}`}
                            disabled={isDisabled}
                            onClick={(event) => {
                                if (!isDisabled) {
                                    this.handleStatusUpdate(row, 'Rejected');
                                }
                            }}>
                            Reject
                        </button>
                        <button
                            className={`ineligible-btn ${isDisabled ? 'disabled' : ''}`}
                            disabled={isDisabled}
                            onClick={(event) => {
                                if (!isDisabled) {
                                    this.handleStatusUpdate(row, 'InEligible');
                                }
                            }}>
                            InEligible
                        </button>
                    </div>
                );
            },
            allowOverflow: false,
            button: true,
            width: '280px'
        },
    ];

    async componentDidMount() {
        ///... check token in local storage
        const accesstoken = await loadFromLocalStorage('accessToken');
        ///... if available get user details otherwise re-direct to login page
        if (accesstoken) {
            this.setState({
                accessToken: accesstoken.toString()
            }, () => {
                this.getRejectedLoans();
            });
        }
        else {
            localStorage.clear();
            window.location.href = '/'
        }
    }

    getRejectedLoans() {
        let pageIndex: number = (this.state.currentPage - 1);
        let sortingObj: any = {};
        if (this.state.currentSortColumn !== '') {
            sortingObj['column'] = this.state.currentSortColumn;
            sortingObj['direction'] = this.state.sortingDirection;
        }
        let pagination: any = {
            "offset": (pageIndex * this.state.currentRowsPerPage),
            "limit": this.state.currentRowsPerPage,
            "sort": { ...sortingObj },
            "searchFilter": this.state.searchText
        };

        ShowBlocker(true);
        ProfileServices.getAllLoanRejectedUser(pagination, this.state.accessToken).then((r) => {
            ShowBlocker(false);
            if (r.ok) {
                r.json().then(resp => {
                    console.log("API Response received:", resp);
                    console.log("Raw rejected loans data:", resp.result);
                    this.setState({ 
                        rejectedLoans: resp.result || [], 
                        totalRows: resp.totalCount || 0 
                    });
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch loan rejection data",
                });
            }
        }).catch((error) => {
            ShowBlocker(false);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "An error occurred while fetching data",
            });
        });
    }

    getTotalDocumentsCount(row: IUserLoanRejection): number {
        let totalCount = 0;
        if (row.documents && row.documents.length > 0) {
            row.documents.forEach((docGroup: IDocumentGroup) => {
                if (docGroup.files && docGroup.files.length > 0) {
                    totalCount += docGroup.files.length;
                }
            });
        }
        return totalCount;
    }

    viewDocuments(row: IUserLoanRejection) {
        const documentData = this.prepareDocumentTableData(row);
        
        MySwal.fire({
            title: `Documents for ${row.userName}`,
            html: this.renderDocumentTableWithActions(documentData, row),
            showCloseButton: true,
            showConfirmButton: false,
            width: '800px',
            customClass: {
                popup: 'document-popup'
            },
            didOpen: () => {
                // Attach event listeners to download links after modal opens
                this.attachDownloadListeners(documentData);
                // Attach event listeners for action buttons
                this.attachPopupActionListeners(row);
            }
        });
    }

    prepareDocumentTableData(row: IUserLoanRejection) {
        console.log("=== PREPARING DOCUMENT DATA FOR USER:", row.userName, "===");
        console.log("Raw row data:", row);
        const documentRows: any[] = [];
        let serialNo = 1;

        if (row.documents && row.documents.length > 0) {
            row.documents.forEach((docGroup: IDocumentGroup, groupIndex) => {
                console.log(`Processing document group ${groupIndex}:`, docGroup);
                if (docGroup.files && docGroup.files.length > 0) {
                    // Find loan application and denial document in the same group
                    const loanApplication = docGroup.files.find((file: IFileDocument) => file.isLoanApplication === true);
                    const denialDocument = docGroup.files.find((file: IFileDocument) => file.isLoanApplication === false);
                    
                    console.log("Found loanApplication:", loanApplication);
                    console.log("Found denialDocument:", denialDocument);
                    
                    const documentRow = {
                        serialNo: serialNo++,
                        loanApplication: loanApplication ? {
                            fileName: loanApplication.fileName,
                            fileId: loanApplication.id
                        } : null,
                        denialDocument: denialDocument ? {
                            fileName: denialDocument.fileName,
                            fileId: denialDocument.id
                        } : null
                    };
                    
                    console.log("Created document row:", documentRow);
                    documentRows.push(documentRow);
                }
            });
        }

        console.log("=== FINAL DOCUMENT ROWS FOR", row.userName, "===", documentRows);
        return documentRows;
    }

    renderDocumentTable(documentData: any[]) {
        if (documentData.length === 0) {
            return '<p>No documents available for this user.</p>';
        }

        let tableHTML = `
            <div style="text-align: left;">
                <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
                    <thead>
                        <tr style="background-color: #f5f5f5;">
                            <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Serial No</th>
                            <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Loan Application</th>
                            <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Denial Document</th>
                        </tr>
                    </thead>
                    <tbody>
        `;

        documentData.forEach(doc => {
            tableHTML += `
                <tr>
                    <td style="border: 1px solid #ddd; padding: 12px;">${doc.serialNo}</td>
                    <td style="border: 1px solid #ddd; padding: 12px;">
                        ${doc.loanApplication ? 
                            `<a href="#" id="loan-${doc.serialNo}" style="color: #007bff; text-decoration: underline; cursor: pointer;">${doc.loanApplication.fileName}</a>` 
                            : '-'}
                    </td>
                    <td style="border: 1px solid #ddd; padding: 12px;">
                        ${doc.denialDocument ? 
                            `<a href="#" id="denial-${doc.serialNo}" style="color: #007bff; text-decoration: underline; cursor: pointer;">${doc.denialDocument.fileName}</a>` 
                            : '-'}
                    </td>
                </tr>
            `;
        });

        tableHTML += `
                    </tbody>
                </table>
            </div>
        `;

        return tableHTML;
    }

    renderDocumentTableWithActions(documentData: any[], row: IUserLoanRejection) {
        const isDisabled = row.status === 'Rejected' || row.status === 'Approved' || row.status === 'InEligible';
        const disabledStyle = isDisabled ? 'opacity: 0.5; cursor: not-allowed; pointer-events: none;' : '';
        
        if (documentData.length === 0) {
            return `
                <div style="text-align: left;">
                    <p>No documents available for this user.</p>
                    <div style="margin-top: 30px; text-align: center;">
                        <div style="display: flex; gap: 10px; justify-content: center;">
                            <button id="popup-approve-btn" style="background-color: #28a745; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500; ${disabledStyle}" ${isDisabled ? 'disabled' : ''}>
                                Approve
                            </button>
                            <button id="popup-reject-btn" style="background-color: #dc3545; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500; ${disabledStyle}" ${isDisabled ? 'disabled' : ''}>
                                Reject
                            </button>
                            <button id="popup-ineligible-btn" style="background-color: #ff9800; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500; ${disabledStyle}" ${isDisabled ? 'disabled' : ''}>
                                InEligible
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }

        let tableHTML = `
            <div style="text-align: left;">
                <div style="margin-bottom: 15px; padding: 10px; background-color: #f8f9fa; border-radius: 4px;">
                    <p style="margin: 0; color: #6c757d;"><strong>Current Status:</strong> <span style="color: #495057;">${row.status}</span></p>
                </div>
                <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
                    <thead>
                        <tr style="background-color: #f5f5f5;">
                            <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Serial No</th>
                            <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Loan Application</th>
                            <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Denial Document</th>
                        </tr>
                    </thead>
                    <tbody>
        `;

        documentData.forEach(doc => {
            tableHTML += `
                <tr>
                    <td style="border: 1px solid #ddd; padding: 12px;">${doc.serialNo}</td>
                    <td style="border: 1px solid #ddd; padding: 12px;">
                        ${doc.loanApplication ? 
                            `<a href="#" id="loan-${doc.serialNo}" style="color: #007bff; text-decoration: underline; cursor: pointer;">${doc.loanApplication.fileName}</a>` 
                            : '-'}
                    </td>
                    <td style="border: 1px solid #ddd; padding: 12px;">
                        ${doc.denialDocument ? 
                            `<a href="#" id="denial-${doc.serialNo}" style="color: #007bff; text-decoration: underline; cursor: pointer;">${doc.denialDocument.fileName}</a>` 
                            : '-'}
                    </td>
                </tr>
            `;
        });

        tableHTML += `
                    </tbody>
                </table>
                <div style="margin-top: 30px; text-align: center; border-top: 1px solid #dee2e6; padding-top: 20px;">
                    <div style="display: flex; gap: 15px; justify-content: center;">
                        <button id="popup-approve-btn" style="background-color: #28a745; color: white; border: none; padding: 12px 24px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500; transition: background-color 0.3s ease; ${disabledStyle}" ${isDisabled ? 'disabled' : ''}>
                            Approve Application
                        </button>
                        <button id="popup-reject-btn" style="background-color: #dc3545; color: white; border: none; padding: 12px 24px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500; transition: background-color 0.3s ease; ${disabledStyle}" ${isDisabled ? 'disabled' : ''}>
                            Reject Application
                        </button>
                        <button id="popup-ineligible-btn" style="background-color: #ff9800; color: white; border: none; padding: 12px 24px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500; transition: background-color 0.3s ease; ${disabledStyle}" ${isDisabled ? 'disabled' : ''}>
                            Mark InEligible
                        </button>
                    </div>
                </div>
            </div>
        `;

        return tableHTML;
    }

    attachDownloadListeners(documentData: any[]) {
        console.log("attachDownloadListeners called with documentData:", documentData);
        documentData.forEach(doc => {
            console.log("Processing document:", doc);
            // Attach listener for loan application
            if (doc.loanApplication) {
                console.log("Loan Application data:", doc.loanApplication);
                const loanLink = document.getElementById(`loan-${doc.serialNo}`);
                if (loanLink) {
                    loanLink.addEventListener('click', (e) => {
                        e.preventDefault();
                        console.log("Loan application clicked, calling downloadFile with:", doc.loanApplication.fileId, doc.loanApplication.fileName);
                        this.downloadFile(doc.loanApplication.fileId, doc.loanApplication.fileName);
                    });
                }
            }

            // Attach listener for denial document
            if (doc.denialDocument) {
                console.log("Denial Document data:", doc.denialDocument);
                const denialLink = document.getElementById(`denial-${doc.serialNo}`);
                if (denialLink) {
                    denialLink.addEventListener('click', (e) => {
                        e.preventDefault();
                        console.log("Denial document clicked, calling downloadFile with:", doc.denialDocument.fileId, doc.denialDocument.fileName);
                        this.downloadFile(doc.denialDocument.fileId, doc.denialDocument.fileName);
                    });
                }
            }
        });
    }

    attachPopupActionListeners(row: IUserLoanRejection) {
        console.log("attachPopupActionListeners called for user:", row.userName);
        const isDisabled = row.status === 'Rejected' || row.status === 'Approved' || row.status === 'InEligible';
        
        // Attach listener for approve button
        const approveBtn = document.getElementById('popup-approve-btn');
        if (approveBtn) {
            approveBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (!isDisabled) {
                    console.log("Popup approve button clicked for user:", row.userId);
                    // Close the popup first
                    MySwal.close();
                    // Then show confirmation and update status
                    this.handleStatusUpdate(row, 'Approved');
                }
            });
        }

        // Attach listener for reject button
        const rejectBtn = document.getElementById('popup-reject-btn');
        if (rejectBtn) {
            rejectBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (!isDisabled) {
                    console.log("Popup reject button clicked for user:", row.userId);
                    // Close the popup first
                    MySwal.close();
                    // Then show confirmation and update status
                    this.handleStatusUpdate(row, 'Rejected');
                }
            });
        }

        // Attach listener for ineligible button
        const ineligibleBtn = document.getElementById('popup-ineligible-btn');
        if (ineligibleBtn) {
            ineligibleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                if (!isDisabled) {
                    console.log("Popup ineligible button clicked for user:", row.userId);
                    // Close the popup first
                    MySwal.close();
                    // Then show confirmation and update status
                    this.handleStatusUpdate(row, 'InEligible');
                }
            });
        }
    }

    downloadFile(fileId: string, fileName: string) {
        console.log("downloadFile called with:", { fileId, fileName });
        const downloadData = {
            id: fileId,
            fileName: fileName
        };
        console.log("Sending download request with data:", downloadData);

        ShowBlocker(true);
        ProfileServices.downloadLoanRejectedFile(downloadData, this.state.accessToken).then((response) => {
            ShowBlocker(false);
            if (response.ok) {
                console.log("downloadLoanRejectedFile",response);
                response.json().then(data => {
                    console.log("Received response data:", data);
                    
                    // Handle different response formats
                    let base64Data = data.body || data.base64Data || data.data || data;
                    
                    if (base64Data && typeof base64Data === 'string') {
                        try {
                            // Clean the base64 string - remove any whitespace and ensure it's properly formatted
                            base64Data = base64Data.replace(/\s/g, '');
                            
                            console.log("Cleaned base64 data length:", base64Data.length);
                            console.log("First 50 chars of base64:", base64Data.substring(0, 50));
                            
                            // Convert base64 to blob
                            const byteCharacters = atob(base64Data);
                            const byteNumbers = new Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const byteArray = new Uint8Array(byteNumbers);
                            const blob = new Blob([byteArray], { type: 'application/pdf' });
                            
                            console.log("Created blob with size:", blob.size);
                            
                            // Create blob link to download
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = fileName;
                            
                            // Append to html link element page
                            document.body.appendChild(link);
                            
                            // Start download
                            link.click();
                            
                            // Clean up and remove the link
                            link.parentNode?.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            
                        } catch (error) {
                            console.error("Error decoding base64 data:", error);
                            console.log("Invalid base64 string:", base64Data.substring(0, 100));
                            Swal.fire({
                                icon: "error",
                                title: "Download Failed",
                                text: "Failed to decode file data. The file may be corrupted.",
                            });
                        }
                    } else {
                        console.error("No valid base64 data found in response:", data);
                        Swal.fire({
                            icon: "error",
                            title: "Download Failed",
                            text: "No file data received from server.",
                        });
                    }
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Download Failed",
                    text: "Failed to download the file. Please try again.",
                });
            }
        }).catch((error) => {
            ShowBlocker(false);
            Swal.fire({
                icon: "error",
                title: "Download Error",
                text: "An error occurred while downloading the file.",
            });
        });
    }

    reviewUserDetails(row: IUserLoanRejection) {
        Swal.fire({
            title: 'User Details',
            html: `
                <div style="text-align: left;">
                    <p><strong>Taxpayer:</strong> ${row.userName}</p>
                    <p><strong>Email:</strong> ${row.email}</p>
                    <p><strong>Mobile Number:</strong> ${row.mobileNumber}</p>
                    <p><strong>Status:</strong> ${row.status}</p>
                    <p><strong>Total Documents:</strong> ${this.getTotalDocumentsCount(row)}</p>
                </div>
            `,
            showConfirmButton: true,
            confirmButtonText: 'Close'
        });
    }

    handleStatusUpdate(row: IUserLoanRejection, newStatus: string) {
        const actionText = newStatus === 'Approved' ? 'approve' : 'reject';
        const confirmButtonText = newStatus === 'Approved' ? 'Yes, Approve' : 'Yes, Reject';
        const confirmButtonColor = newStatus === 'Approved' ? '#28a745' : '#dc3545';

        Swal.fire({
            title: `${newStatus === 'Approved' ? 'Approve' : 'Reject'} Loan Application`,
            html: `
                <div style="text-align: left;">
                    <p>Are you sure you want to <strong>${actionText}</strong> the loan application for:</p>
                    <br>
                    <p><strong>Taxpayer:</strong> ${row.userName}</p>
                    <p><strong>Email:</strong> ${row.email}</p>
                    <p><strong>Current Status:</strong> ${row.status}</p>
                    <p><strong>New Status:</strong> ${newStatus}</p>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: confirmButtonColor,
            cancelButtonColor: '#6c757d',
            confirmButtonText: confirmButtonText,
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                this.updateLoanStatus(row.userId, newStatus);
            }
        });
    }

    updateLoanStatus(userId: string, status: string) {
        const requestData = {
            userId: userId,
            status: status
        };

        ShowBlocker(true);
        ProfileServices.updateLoanRejectedFilesStatus(requestData, this.state.accessToken).then((response) => {
            ShowBlocker(false);
            if (response.ok) {
                response.json().then(data => {
                    Swal.fire({
                        icon: "success",
                        title: "Success",
                        text: `Loan application has been ${status.toLowerCase()} successfully.`,
                    }).then(() => {
                        // Refresh the data after successful update
                        this.getRejectedLoans();
                    });
                });
            } else {
                response.json().then(errorData => {
                    Swal.fire({
                        icon: "error",
                        title: "Update Failed",
                        text: errorData.message || `Failed to ${status.toLowerCase()} the loan application. Please try again.`,
                    });
                }).catch(() => {
                    Swal.fire({
                        icon: "error",
                        title: "Update Failed",
                        text: `Failed to ${status.toLowerCase()} the loan application. Please try again.`,
                    });
                });
            }
        }).catch((error) => {
            ShowBlocker(false);
            console.error("Error updating loan status:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "An error occurred while updating the loan status.",
            });
        });
    }

    identifySortingMapColumn(columnName: string) {
        let actualColName = '';
        switch (columnName) {
            case "Taxpayer Name":
                actualColName = 'userName';
                break;
            case "Email":
                actualColName = 'email';
                break;
            case "Mobile Number":
                actualColName = 'mobileNumber';
                break;
            case "Status":
                actualColName = 'status';
                break;
        }
        return actualColName;
    }

    public render() {
        return (
            <div className='body-sub-contianer table-container'>
                <DataGrid
                    title='User Loan Rejections'
                    search='Search Rejected Loans'
                    columns={this.formColumns}
                    data={this.state.rejectedLoans}
                    onSearch={(searchText_: string) => {
                        this.setState({
                            searchText: searchText_,
                            currentPage: 1
                        }, () => {
                            this.getRejectedLoans();
                        })
                    }}

                    onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                        this.setState({
                            currentPage: currentPage,
                            currentRowsPerPage: currentRowsPerPage
                        }, () => {
                            this.getRejectedLoans();
                        })
                    }}
                    onChangePage={(page: number, totalRows: number) => {
                        console.log(page, totalRows);
                        this.setState({
                            currentPage: page
                        }, () => {
                            this.getRejectedLoans();
                        })
                    }}
                    onSort={(column: any, direction) => {
                        this.setState({
                            currentSortColumn: this.identifySortingMapColumn(column.name),
                            sortingDirection: direction,
                            currentPage: 1
                        }, () => {
                            this.getRejectedLoans();
                        })
                    }}
                    pagination
                    paginationTotalRows={this.state.totalRows}
                />
            </div>
        )
    }
}

export default UserLoanRejection;
